import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

const fmt      = (cents) => `$${(cents / 100).toFixed(2)}`;
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString() : '—';

const STATUS_BADGE = {
  pending:  { bg: '#fef9c3', color: '#713f12', label: 'Pending' },
  active:   { bg: '#dcfce7', color: '#059669', label: 'Active' },
  paused:   { bg: '#e0e7ff', color: '#4338ca', label: 'Paused' },
  rejected: { bg: '#fee2e2', color: '#dc2626', label: 'Rejected' },
};

const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [overview, setOverview]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (filter)       params.set('search', filter);
      if (statusFilter) params.set('status', statusFilter);
      const [affRes, overRes] = await Promise.all([
        API.get(`/admin/affiliates?${params}`),
        API.get('/admin/affiliates/overview'),
      ]);
      setAffiliates(affRes.data.affiliates || []);
      setTotal(affRes.data.total || 0);
      setOverview(overRes.data.overview || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Affiliates">

      {/* Overview cards */}
      {overview && (
        <div className="row g-3 mb-4">
          {[
            { label: 'Total Affiliates', value: overview.total },
            { label: 'Active',           value: overview.active },
            { label: 'Pending Review',   value: overview.pending },
            { label: 'Total Conversions',value: overview.totalConversions || 0 },
            { label: 'Revenue Driven',   value: fmt(overview.totalRevenue  || 0) },
            { label: 'Commission Pending',value: fmt(overview.totalPending || 0) },
          ].map((s, i) => (
            <div key={i} className="col-6 col-md-2">
              <div className="card border-0 shadow-sm text-center p-3" style={{ borderRadius: '12px' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-main)' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <div className="card-body p-3 d-flex gap-2 flex-wrap">
          <input
            className="form-control rounded-3 flex-grow-1"
            placeholder="Search by name, email or code…"
            value={filter}
            onChange={e => { setFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: '280px', fontSize: '14px' }}
          />
          <select
            className="form-select rounded-3"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: '160px', fontSize: '14px' }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
            </div>
          ) : affiliates.length === 0 ? (
            <div className="text-center py-5" style={{ color: 'var(--text-soft)', fontSize: '14px' }}>
              No affiliates found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: '13px' }}>
                <thead style={{ background: 'var(--page-bg)' }}>
                  <tr style={{ color: 'var(--text-muted2)' }}>
                    <th className="px-4 py-3">Affiliate</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>Revenue</th>
                    <th>Pending</th>
                    <th>Applied</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map(a => {
                    const badge = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
                    return (
                      <tr key={a._id}>
                        <td className="px-4 py-3">
                          <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{a.name}</div>
                          <div style={{ color: 'var(--text-soft)', fontSize: '12px' }}>{a.email}</div>
                        </td>
                        <td>
                          <span style={{ fontWeight: 700, letterSpacing: '1px', color: '#6f42c1', fontSize: '13px' }}>
                            {a.code}
                          </span>
                        </td>
                        <td>
                          <span style={{ background: badge.bg, color: badge.color, padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                            {badge.label}
                          </span>
                        </td>
                        <td>{a.stats.clicks}</td>
                        <td>{a.stats.conversions}</td>
                        <td>{fmt(a.stats.revenueTotal)}</td>
                        <td style={{ color: '#ea580c', fontWeight: 600 }}>{fmt(a.stats.commissionPending)}</td>
                        <td style={{ color: 'var(--text-soft)' }}>{fmtDate(a.createdAt)}</td>
                        <td>
                          <Link
                            to={`/admin/affiliates/${a._id}`}
                            className="btn btn-sm rounded-pill"
                            style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '12px' }}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <button className="btn btn-sm rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1' }}
            disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: '13px', color: 'var(--text-muted2)', alignSelf: 'center' }}>
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <button className="btn btn-sm rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1' }}
            disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAffiliates;
